import { useState, useMemo } from "react";
import { Spinner } from "@chakra-ui/react";
import Modal from "newui/components/ui/Modal/Modal";
import { Button } from "newui/components/Button";
import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import type { Clarification } from "../../types/clarification.type";

interface Supplier {
	id: number;
	name: string;
	logo?: string | null;
}

interface CopyToSuppliersModalProps {
	clarification: Clarification | null;
	isLoading: boolean;
	isOpen: boolean;
	suppliers: Supplier[];
	onClose: () => void;
	onConfirm: (clarificationId: number, rfxInviteIds: number[]) => void;
}

export const CopyToSuppliersModal: React.FC<CopyToSuppliersModalProps> = ({
	isOpen,
	clarification,
	suppliers,
	isLoading,
	onClose,
	onConfirm,
}) => {
	const [selectedIds, setSelectedIds] = useState<number[]>([]);

	const availableSuppliers = useMemo(() => {
		if (!clarification) return suppliers;
		return suppliers.filter((s) => s.id !== clarification.rfx_submission_id);
	}, [suppliers, clarification]);

	const handleToggle = (id: number) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
		);
	};

	const handleSelectAll = () => {
		if (selectedIds.length === availableSuppliers.length) {
			setSelectedIds([]);
		} else {
			setSelectedIds(availableSuppliers.map((s) => s.id));
		}
	};

	const handleConfirm = () => {
		if (!clarification || selectedIds.length === 0) return;
		onConfirm(clarification.id, selectedIds);
	};

	const handleClose = () => {
		setSelectedIds([]);
		onClose();
	};

	if (!clarification) return null;

	return (
		<Modal isOpen={isOpen} onClose={handleClose} title="Copy to Suppliers" size="md" iconType="create">
			<div className="space-y-4 p-4">
				<p className="text-sm text-gray-600">
					Select which suppliers to copy this clarification to:
				</p>

				<div className="border rounded-lg divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
					<label className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-200 bg-gray-50">
						<input
							type="checkbox"
							checked={selectedIds.length === availableSuppliers.length && availableSuppliers.length > 0}
							onChange={handleSelectAll}
							className="h-4 w-4 rounded border-gray-300 text-blue-600"
						/>
						<span className="text-sm font-medium text-gray-700">Select All</span>
					</label>
					{availableSuppliers.map((supplier) => (
						<label
							key={supplier.id}
							className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer"
						>
							<input
								type="checkbox"
								checked={selectedIds.includes(supplier.id)}
								onChange={() => handleToggle(supplier.id)}
								className="h-4 w-4 rounded border-gray-300 text-blue-600"
							/>
							<FallbackImage
								src={supplier.logo || ""}
								alt={supplier.name}
								className="h-6 w-6 rounded-full object-cover"
							/>
							<span className="text-sm text-gray-700">{supplier.name}</span>
						</label>
					))}
				</div>

				{availableSuppliers.length === 0 && (
					<p className="text-sm text-gray-400 text-center py-4">
						No other suppliers available.
					</p>
				)}

				<div className="flex justify-end gap-3 pt-2">
					<Button btntype="base" onClick={handleClose} disabled={isLoading}>
						Cancel
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={selectedIds.length === 0 || isLoading}
					>
						{isLoading ? (
							<Spinner size="sm" />
						) : (
							`Copy to ${selectedIds.length} Supplier${selectedIds.length !== 1 ? "s" : ""}`
						)}
					</Button>
				</div>
			</div>
		</Modal>
	);
};
